import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Единая форма документа — создание и правка. Раньше создание жило модалкой на
// странице списка, а правка — отдельной инлайн-формой в левой колонке карточки:
// два набора полей, две валидации, разные подписи кнопок.
//
// Текст документа правится на карточке и остаётся там: это основная рабочая
// область страницы, а не поле формы.
import { useEffect, type ReactElement } from 'react';
import { Alert, Form, Input, InputNumber, Modal, Select } from 'antd';

import { EntityAttachmentsPanel } from '../../attachments/components/EntityAttachmentsPanel.js';
import { EntityLinkFields } from '../../entity-links/EntityLinkFields.js';
import { useEntityLinkCascade } from '../../entity-links/useEntityLinkCascade.js';
import type {
  CreateDocumentInput,
  DocumentListItem,
  DocumentStatus,
  DocumentType,
} from '../api/documents.client.js';
import {
  useCreateDocumentMutation,
  useUpdateDocumentMutation,
} from '../hooks/useDocumentMutations.js';

export const DOCUMENT_TYPE_OPTIONS: { value: DocumentType; label: string }[] = [
  {
    value: 'contract',
    get label() {
      return i18n.t('crm.features.map.features.documents.components.documentformmodal.47');
    },
  },
  {
    value: 'invoice',
    get label() {
      return i18n.t('crm.features.map.features.documents.components.documentformmodal.48');
    },
  },
  {
    value: 'act',
    get label() {
      return i18n.t('crm.features.map.features.documents.components.documentformmodal.49');
    },
  },
];

export const DOCUMENT_STATUS_OPTIONS: { value: DocumentStatus; label: string }[] = [
  {
    value: 'draft',
    get label() {
      return i18n.t('crm.features.map.features.documents.components.documentformmodal.50');
    },
  },
  {
    value: 'sent',
    get label() {
      return i18n.t('crm.features.map.features.documents.components.documentformmodal.51');
    },
  },
  {
    value: 'signed',
    get label() {
      return i18n.t('crm.features.map.features.documents.components.documentformmodal.52');
    },
  },
  {
    value: 'cancelled',
    get label() {
      return i18n.t('crm.features.map.features.documents.components.documentformmodal.53');
    },
  },
];

interface DocumentFormValues {
  title: string;
  type: DocumentType;
  status?: DocumentStatus;
  amount?: number | null;
  /** Три поля привязки — те же имена, что ждёт общий каскад. */
  companyId?: string | undefined;
  contactId?: string | undefined;
  dealId?: string | undefined;
}

interface DocumentFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Передан — режим правки; иначе создание. */
  document?: DocumentListItem | null;
  /** Создание: куда перейти после успеха (карточка нового документа). */
  onCreated?: (documentId: string) => void;
  lockedContactId?: string;
  /** Создание с карточки компании: компания проставлена жёстко, поле скрыто. */
  lockedCompanyId?: string;
  lockedDealId?: string;
}

export function DocumentFormModal({
  open,
  onClose,
  document,
  onCreated,
  lockedContactId,
  lockedCompanyId,
  lockedDealId,
}: DocumentFormModalProps): ReactElement {
  useI18nSubscription();
  const [form] = Form.useForm<DocumentFormValues>();
  const isEdit = Boolean(document);

  // Тот же каскад, что в формах задачи и встречи: компания сужает список
  // контактов, а контакт и компания вместе — список сделок.
  const cascade = useEntityLinkCascade<DocumentFormValues>(form);
  const createMutation = useCreateDocumentMutation();
  const updateMutation = useUpdateDocumentMutation();
  const pending = createMutation.isPending || updateMutation.isPending;
  const error = (createMutation.error ?? updateMutation.error) as Error | null;

  useEffect(() => {
    if (!open) return;
    createMutation.reset();
    updateMutation.reset();
    form.setFieldsValue({
      title: document?.title ?? '',
      type: document?.type ?? 'contract',
      status: document?.status ?? 'draft',
      amount:
        document?.amount === null || document?.amount === undefined
          ? null
          : Number(document.amount),
      companyId: lockedCompanyId ?? document?.companyId ?? undefined,
      contactId: lockedContactId ?? document?.contactId ?? undefined,
      dealId: lockedDealId ?? document?.dealId ?? undefined,
    });
  }, [open, document, form, lockedContactId, lockedCompanyId, lockedDealId]);

  const handleSubmit = (values: DocumentFormValues): void => {
    const contactId = lockedContactId ?? values.contactId;
    const companyId = lockedCompanyId ?? values.companyId;
    const dealId = lockedDealId ?? values.dealId;
    if (isEdit && document) {
      // Пустое поле в правке — «очистить», поэтому null, а не пропуск ключа.
      updateMutation.mutate(
        {
          id: document.id,
          input: {
            title: values.title.trim(),
            type: values.type,
            ...(values.status ? { status: values.status } : {}),
            amount: values.amount ?? null,
            companyId: companyId ?? null,
            contactId: contactId ?? null,
            dealId: dealId ?? null,
          },
        },
        { onSuccess: () => onClose() },
      );
      return;
    }

    const input: CreateDocumentInput = {
      title: values.title.trim(),
      type: values.type,
      ...(values.amount === null || values.amount === undefined ? {} : { amount: values.amount }),
      ...(companyId ? { companyId } : {}),
      ...(contactId ? { contactId } : {}),
      ...(dealId ? { dealId } : {}),
    };
    createMutation.mutate(input, {
      onSuccess: (created) => {
        onClose();
        onCreated?.(created.id);
      },
    });
  };

  return (
    <Modal
      title={
        isEdit
          ? i18n.t(
              'crm.features.features.documents.components.documentformmodal.редактировать_документ',
            )
          : i18n.t('crm.features.features.documents.components.documentformmodal.новый_документ')
      }
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={
        isEdit
          ? i18n.t('crm.features.features.documents.components.documentformmodal.сохранить')
          : i18n.t('crm.features.features.documents.components.documentformmodal.создать')
      }
      cancelText={i18n.t('crm.features.features.documents.components.documentformmodal.отмена')}
      confirmLoading={pending}
      width={560}
      destroyOnHidden
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          title={
            isEdit
              ? i18n.t(
                  'crm.features.features.documents.components.documentformmodal.не_удалось_сохранить_документ',
                )
              : i18n.t(
                  'crm.features.features.documents.components.documentformmodal.не_удалось_создать_документ',
                )
          }
          description={error.message}
        />
      ) : null}
      <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={pending}>
        <Form.Item
          name="title"
          label={i18n.t(
            'crm.features.features.documents.components.documentformmodal.название_документа',
          )}
          rules={[
            {
              required: true,
              whitespace: true,
              message: i18n.t(
                'crm.features.features.documents.components.documentformmodal.введите_название_документа',
              ),
            },
          ]}
        >
          <Input
            placeholder={i18n.t(
              'crm.features.features.documents.components.documentformmodal.договор',
            )}
            maxLength={200}
            autoFocus
          />
        </Form.Item>

        <div className="flex gap-3">
          <Form.Item
            name="type"
            label={i18n.t('crm.features.features.documents.components.documentformmodal.тип')}
            className="flex-1"
            rules={[
              {
                required: true,
                message: i18n.t(
                  'crm.features.features.documents.components.documentformmodal.выберите_тип_документа',
                ),
              },
            ]}
          >
            <Select options={DOCUMENT_TYPE_OPTIONS} />
          </Form.Item>
          {isEdit ? (
            <Form.Item
              name="status"
              label={i18n.t('crm.features.features.documents.components.documentformmodal.статус')}
              className="flex-1"
            >
              <Select options={DOCUMENT_STATUS_OPTIONS} />
            </Form.Item>
          ) : null}
          <Form.Item
            name="amount"
            label={i18n.t('crm.features.features.documents.components.documentformmodal.сумма')}
            className="flex-1"
          >
            <InputNumber<number>
              className="w-full"
              min={0}
              step={1000}
              placeholder="0"
              addonAfter="₽"
            />
          </Form.Item>
        </div>

        {/* Компания, контакт и сделка — с кем заключён документ. */}
        <EntityLinkFields
          cascade={cascade}
          lockedContactId={lockedContactId}
          lockedCompanyId={lockedCompanyId}
          lockedDealId={lockedDealId}
        />
      </Form>

      {/* Файл прикрепляется к существующей записи: до создания документа нет id,
          к которому привязать вложение, и ключа объекта в хранилище. */}
      {isEdit && document ? (
        <div className="-mx-6 border-t border-gray-200">
          <EntityAttachmentsPanel
            entity="document"
            entityId={document.id}
            title={i18n.t(
              'crm.features.features.documents.components.documentformmodal.файлы_документа',
            )}
          />
        </div>
      ) : (
        <div className="mt-2 text-xs text-gray-500">
          {i18n.t(
            'crm.features.features.documents.components.documentformmodal.файлы_можно_будет_прикрепить_сразу_после_с',
          )}
        </div>
      )}
    </Modal>
  );
}
